import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import {
  savingsSuggestion,
  type AccountWithBuckets,
  type SavingsFeasibility,
  type SavingsGoalView,
} from '@financial/shared';
import {
  useContributeMoney,
  useCreateGoal,
  useDeleteGoal,
  useManualGoalCorrection,
  useSavings,
  useUpdateGoal,
} from '../features/savings/useSavings';
import { useAccounts } from '../features/accounts/useAccounts';
import { useBudget } from '../features/budgets/useBudgets';
import { useCard } from '../features/creditCard/useCreditCard';
import { currentCurrency, formatDate, formatMoney, toDateInputValue } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { getPurchasePolicy, safeExtraSavings } from '../lib/purchaseCapacity';
import { Field } from '../components/ui/Field';
import { DatePicker, MonthPicker } from '../components/ui/DatePicker';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ItemActionsMenu } from '../components/ui/ItemActionsMenu';
import {
  Button,
  EmptyState,
  FilterChipGroup,
  HelperText,
  Skeleton,
} from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { currentYearMonth } from '../lib/businessDate';

const FEASIBILITY: Record<SavingsFeasibility, { label: string; color: string }> = {
  on_track: { label: 'En camino', color: '#15803d' },
  tight: { label: 'Ajustado', color: '#b45309' },
  not_feasible: { label: 'No alcanza a tiempo', color: '#b91c1c' },
  achieved: { label: '¡Meta lograda!', color: '#15803d' },
};

export function SavingsGoalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMonth = searchParams.get('mes');
  const budgetMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(requestedMonth ?? '')
    ? requestedMonth!
    : currentYearMonth();
  const listQ = useSavings();
  const accountsQ = useAccounts();
  const budgetQ = useBudget(budgetMonth);
  const cardQ = useCard();
  const [addOpen, setAddOpen] = useState(false);
  const [status, setStatus] = useState<'active' | 'achieved' | 'all'>('active');
  const goals = useMemo(() => {
    const items = listQ.data ?? [];
    const availablePerMonth = budgetQ.data?.breakdown.savingsTarget;
    if (availablePerMonth === undefined) return items;
    return items.map((goal) => {
      const result = savingsSuggestion({
        targetAmount: goal.targetAmount,
        saved: goal.saved,
        targetDate: goal.targetDate,
        availablePerMonth,
      });
      return {
        ...goal,
        feasibility: result.feasibility,
        achievableDate: result.achievableDate,
      };
    });
  }, [budgetQ.data?.breakdown.savingsTarget, listQ.data]);
  const accounts = accountsQ.data ?? [];
  const visibleGoals = useMemo(() => {
    const achieved = (goal: SavingsGoalView) =>
      goal.feasibility === 'achieved' || goal.progress >= 1;
    if (status === 'active') return goals.filter((goal) => !achieved(goal));
    if (status === 'achieved') return goals.filter(achieved);
    return goals;
  }, [goals, status]);
  const activeGoals = useMemo(
    () => goals.filter((goal) => goal.feasibility !== 'achieved' && goal.progress < 1),
    [goals],
  );
  const safeContribution = budgetQ.data
    ? cardQ.data
      ? getPurchasePolicy(cardQ.data, budgetQ.data).cashCap
      : safeExtraSavings(budgetQ.data.availableFree)
    : undefined;
  const selectBudgetMonth = (nextMonth: string) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('mes', nextMonth || currentYearMonth());
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    setAddOpen(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('nuevo');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="goals"
        title="Metas de ahorro"
        description="Define un objetivo y respáldalo con dinero que mueves a una cuenta o apartado."
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <MonthPicker
              id="goals-budget-month"
              value={budgetMonth}
              onChange={selectBudgetMonth}
              className="w-full sm:w-[280px] sm:max-w-full"
              helperText="Mes usado para calcular cuánto puedes destinar a tus metas."
            />
            <Button onClick={() => setAddOpen(true)}>+ Meta</Button>
          </div>
        }
      />

      <p className="mb-3 text-xs leading-5 text-slate-500">
        La capacidad que aparece abajo usa el presupuesto del mes elegido; el avance de cada meta
        muestra lo que tienes registrado hoy.
      </p>

      {budgetQ.data && (
        <SavingsCapacityCard
          amount={safeContribution ?? 0}
          budget={budgetQ.data.availableFree}
          month={budgetMonth}
        />
      )}

      {budgetQ.data && activeGoals.length > 0 && (
        <GoalsPlanCard
          goals={activeGoals}
          monthlyCapacity={budgetQ.data.breakdown.savingsTarget}
          committedSavings={budgetQ.data.breakdown.committedSavings}
          month={budgetMonth}
        />
      )}

      <div className="savings-goals-content">
        {goals.length > 0 && (
          <FilterChipGroup
            className="mb-4"
            value={status}
            ariaLabel="Filtrar metas por estado"
            options={[
              { value: 'active' as const, label: 'En curso' },
              { value: 'achieved' as const, label: 'Logradas' },
              { value: 'all' as const, label: 'Todas' },
            ]}
            onChange={setStatus}
          />
        )}

        {listQ.isLoading && <SavingsGoalsSkeleton />}
        {goals.length === 0 && !listQ.isLoading && (
          <EmptyState
            title="Crea tu primera meta"
            description="Al registrar monto y fecha, verás el ahorro sugerido por mes."
            action={<Button onClick={() => setAddOpen(true)}>Crear mi primera meta</Button>}
          />
        )}

        {goals.length > 0 && visibleGoals.length === 0 && !listQ.isLoading && (
          <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No hay metas en este estado.
          </p>
        )}
        <div className="grid gap-4">
          {visibleGoals.map((g) => (
            <GoalCard key={g.id} goal={g} accounts={accounts} safeContribution={safeContribution} />
          ))}
        </div>
      </div>

      <Modal open={addOpen} title="Nueva meta" onClose={() => setAddOpen(false)}>
        <GoalForm onDone={() => setAddOpen(false)} />
      </Modal>
    </div>
  );
}

export function SavingsGoalsSkeleton() {
  return (
    <SkeletonState label="Cargando metas de ahorro…" className="grid gap-4">
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-44 w-full" />
      ))}
    </SkeletonState>
  );
}

/**
 * Una tarjeta de meta explica el ritmo de ESA meta. Esta vista evita la
 * conclusión peligrosa de sumar varias promesas individuales como si cada una
 * tuviera una bolsa de dinero distinta.
 */
function GoalsPlanCard({
  goals,
  monthlyCapacity,
  committedSavings,
  month,
}: {
  goals: SavingsGoalView[];
  monthlyCapacity: number;
  committedSavings: number;
  month: string;
}) {
  const required = goals.reduce((sum, goal) => sum + goal.suggestedMonthly, 0);
  const gap = Math.max(0, required - monthlyCapacity);
  const emergencyGoal = goals.find((goal) => /fondo\s+de\s+emergencia/i.test(goal.name));
  const houseGoal = goals.find((goal) => /enganche.*casa|casa.*enganche/i.test(goal.name));
  const fitsTogether = gap <= 0.005;

  return (
    <section
      className={`mb-4 overflow-hidden rounded-[18px] border p-4 shadow-[0_12px_28px_-20px_rgba(15,23,42,.22)] sm:p-5 ${
        fitsTogether
          ? 'border-emerald-100 bg-[linear-gradient(120deg,#ecfdf5_0%,#fff_70%,#eff6ff_100%)]'
          : 'border-amber-200 bg-[linear-gradient(120deg,#fffbeb_0%,#fff_70%,#eff6ff_100%)]'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-savings">
            Plan conjunto
          </p>
          <h2 className="mt-1 text-lg font-bold tracking-tight text-slate-900">
            ¿Caben todas tus metas al mismo tiempo?
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-5 text-slate-600">
            Cada meta calcula su propio ritmo. Aquí las sumamos contra una sola capacidad mensual
            para evitar prometer el mismo ahorro dos veces.
          </p>
        </div>
        <Link
          to={`/presupuesto?mes=${encodeURIComponent(month)}`}
          className="button button-secondary"
        >
          Ver capacidad
        </Link>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <PlanMetric label="Ritmo de todas las metas" value={`${formatMoney(required)}/mes`} />
        <PlanMetric
          label="Ahorro protegido al mes"
          value={`${formatMoney(monthlyCapacity)}/mes`}
          tone={fitsTogether ? 'good' : 'neutral'}
        />
        <PlanMetric
          label={fitsTogether ? 'Dinero sin asignar' : 'Diferencia por resolver'}
          value={`${formatMoney(Math.abs(monthlyCapacity - required))}/mes`}
          tone={fitsTogether ? 'good' : 'risk'}
        />
      </div>

      {fitsTogether ? (
        <p className="mt-3 text-xs leading-5 text-slate-600">
          Tus calendarios caben dentro del ahorro que el presupuesto protege. Aun así, conserva el
          colchón operativo y revisa el plan si cambian tus ingresos o gastos esenciales.
        </p>
      ) : emergencyGoal && houseGoal ? (
        <div className="mt-3 rounded-xl border border-amber-100 bg-white/75 p-3 text-sm leading-5 text-slate-700">
          <p className="font-semibold text-slate-800">
            Orden recomendado: primero el fondo de emergencia; después el enganche.
          </p>
          <p className="mt-1">
            No uses el dinero del fondo para el enganche. Cuando el fondo esté completo, redirige el
            ahorro recurrente que hoy ya apartas ({formatMoney(committedSavings)}/mes) al enganche y
            revisa su fecha o el monto mensual restante.
          </p>
        </div>
      ) : (
        <p className="mt-3 rounded-xl border border-amber-100 bg-white/75 p-3 text-xs leading-5 text-slate-700">
          No caben todas las metas con el ahorro actual. Elige cuál tiene prioridad, aumenta el
          ahorro sostenible o mueve una fecha objetivo antes de asumir más deuda.
        </p>
      )}
    </section>
  );
}

function PlanMetric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'good' | 'risk' | 'neutral';
}) {
  const color =
    tone === 'good' ? 'text-income' : tone === 'risk' ? 'text-expense' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-white/90 bg-white/85 p-3.5 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-[.07em] text-slate-600">{label}</p>
      <p className={`mt-1 text-xl font-bold tracking-tight tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

function SavingsCapacityCard({
  amount,
  budget,
  month,
}: {
  amount: number;
  budget: number;
  month: string;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-[18px] border border-emerald-100 bg-[linear-gradient(120deg,#ecfdf5_0%,#fff_68%,#eff6ff_100%)] p-4 shadow-[0_12px_28px_-20px_rgba(5,150,105,.3)] sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-savings">
            Decisión de ahorro
          </p>
          <h2 className="mt-1 text-lg font-bold tracking-tight text-slate-900">
            Aporte adicional prudente dentro de tu límite mensual
          </h2>
          <p className="mt-1 max-w-xl text-sm leading-5 text-slate-600">
            Además de lo que ya tienes apartado, puedes destinar hasta esta cantidad a una meta sin
            consumir todo lo que queda de tu límite mensual de gasto.
          </p>
        </div>
        <Link
          to={`/presupuesto?mes=${encodeURIComponent(month)}`}
          className="button button-secondary"
        >
          Ver presupuesto
        </Link>
      </div>
      <div className="mt-4 rounded-xl border border-white/90 bg-white/85 p-3.5 shadow-sm sm:max-w-sm">
        <p className="text-[11px] font-bold uppercase tracking-[.07em] text-slate-600">
          Aporte prudente
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-income">{formatMoney(amount)}</p>
        <p className="mt-1 text-xs leading-4 text-slate-600">
          Conserva {formatMoney(Math.max(0, budget - amount))} como colchón ante variaciones del
          mes.
        </p>
      </div>
      <HelperText className="mt-3" tone="info">
        Es una cantidad máxima sugerida según lo que queda de tu límite, no el saldo de tus cuentas
        ni una obligación. Si la usas en una meta, no la uses también para una compra nueva.
      </HelperText>
    </section>
  );
}

interface MoneyLocation {
  value: string;
  account: AccountWithBuckets;
  bucket?: AccountWithBuckets['buckets'][number];
}

function locationsFrom(accounts: AccountWithBuckets[]): MoneyLocation[] {
  return accounts.flatMap((account) => [
    { value: account.id, account },
    ...account.buckets.map((bucket) => ({ value: `${account.id}:${bucket.id}`, account, bucket })),
  ]);
}

function parseLocation(value: string): { accountId: string; bucketId?: string } {
  const [accountId, bucketId] = value.split(':');
  return { accountId, bucketId: bucketId || undefined };
}

function availableAt(location: MoneyLocation): number {
  if (location.bucket) return location.bucket.balance;
  return Math.max(
    0,
    location.account.balance -
      location.account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0),
  );
}

function GoalCard({
  goal,
  accounts,
  safeContribution,
}: {
  goal: SavingsGoalView;
  accounts: AccountWithBuckets[];
  safeContribution?: number;
}) {
  const contribute = useContributeMoney();
  const correct = useManualGoalCorrection();
  const del = useDeleteGoal();
  const [editOpen, setEditOpen] = useState(false);
  const [contributionOpen, setContributionOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const feas = FEASIBILITY[goal.feasibility];
  const pct = Math.round(goal.progress * 100);
  const chartData = [{ name: goal.name, value: pct, fill: feas.color }];
  const linkedLocations = locationsFrom(accounts).filter(
    (location) => location.bucket?.goalId === goal.id,
  );
  const backedFromUi = linkedLocations.reduce(
    (sum, location) => sum + (location.bucket?.balance ?? 0),
    0,
  );
  const backedAmount = goal.backedAmount ?? backedFromUi;
  const unbackedAmount = goal.unbackedAmount ?? goal.saved - backedAmount;
  const isEmergencyFund = /fondo de emergencia/i.test(goal.name);
  const isHouseDownPayment = /enganche.*casa/i.test(goal.name);

  return (
    <div className="ui-card">
      <div className="flex items-start justify-between gap-3">
        <h2 className="min-w-0 truncate font-semibold text-slate-900">{goal.name}</h2>
        <ItemActionsMenu
          itemLabel={goal.name}
          title="Opciones de la meta"
          actions={[
            {
              label: 'Editar meta',
              description: 'Cambia el nombre, objetivo o fecha.',
              onSelect: () => setEditOpen(true),
            },
            {
              label: 'Eliminar meta',
              description: 'El dinero y los traspasos conservarán su historial.',
              tone: 'danger',
              onSelect: () => setDeleteOpen(true),
            },
          ]}
        />
      </div>

      <div className="relative mx-auto h-40 w-40">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="72%"
            outerRadius="100%"
            data={chartData}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={8} background />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-900">{pct}%</span>
          <span className="text-xs" style={{ color: feas.color }}>
            {feas.label}
          </span>
        </div>
      </div>

      <div className="mt-2 text-center text-sm">
        <p className="font-medium text-slate-800">
          {formatMoney(goal.saved)}{' '}
          <span className="text-slate-600">de {formatMoney(goal.targetAmount)}</span>
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Ahorra{' '}
          <span className="font-semibold text-savings">{formatMoney(goal.suggestedMonthly)}</span>
          /mes
          {' · '}
          meta {formatDate(goal.targetDate)}
        </p>
        {goal.feasibility === 'not_feasible' && goal.achievableDate && (
          <p className="mt-1 text-xs text-expense">
            A este ritmo la lograrías hasta {formatDate(goal.achievableDate)}.
          </p>
        )}
      </div>

      {isEmergencyFund && (
        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/70 px-3 py-2.5 text-xs leading-4 text-slate-600">
          Base configurada: 6 meses × {formatMoney(20000)} de gastos esenciales. Revísala si cambian
          tu renta, servicios o dependientes.
        </div>
      )}

      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs leading-4 text-slate-600">
        <p className="font-semibold text-slate-700">Dinero respaldado</p>
        {linkedLocations.length > 0 ? (
          <>
            <p className="mt-0.5 font-semibold text-slate-800">
              {formatMoney(backedAmount)} en apartados
            </p>
            <p className="mt-0.5">
              {linkedLocations
                .map((location) => `${location.account.name} › ${location.bucket?.name}`)
                .join(' · ')}
            </p>
          </>
        ) : (
          <p className="mt-0.5">
            No hay dinero respaldado todavía. Al aportar, elige un apartado para que quede
            reservado.
          </p>
        )}
        {Math.abs(unbackedAmount) > 0.005 && (
          <p
            className={`mt-2 rounded-lg px-2 py-1.5 ${unbackedAmount > 0 ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700'}`}
          >
            Ajuste sin respaldo: {unbackedAmount > 0 ? '+' : ''}
            {formatMoney(unbackedAmount)}. No es dinero disponible hasta vincularlo con un apartado.
            {goal.lastManualCorrectionReason ? ` Motivo: ${goal.lastManualCorrectionReason}.` : ''}
          </p>
        )}
        {isHouseDownPayment && linkedLocations.length > 0 && (
          <p className="mt-1 text-slate-600">
            Este saldo es independiente del fondo de emergencia.
          </p>
        )}
      </div>

      <HelperText className="mt-3" tone={goal.feasibility === 'not_feasible' ? 'warning' : 'info'}>
        El cálculo supone que ahorras la misma cantidad con frecuencia. Aportar registra un
        movimiento real entre tus cuentas; corregir el saldo sólo cambia el dato mostrado.
      </HelperText>

      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
        <Button type="button" onClick={() => setContributionOpen(true)}>
          Aportar dinero
        </Button>
        <Button type="button" variant="secondary" onClick={() => setCorrectionOpen(true)}>
          Corregir saldo
        </Button>
      </div>

      <Modal
        open={contributionOpen}
        title="Aportar a meta"
        onClose={() => setContributionOpen(false)}
      >
        <GoalContributionForm
          goal={goal}
          accounts={accounts}
          safeContribution={safeContribution}
          pending={contribute.isPending}
          error={contribute.error}
          onSubmit={(body) =>
            contribute.mutate(
              { id: goal.id, body },
              { onSuccess: () => setContributionOpen(false) },
            )
          }
        />
      </Modal>
      <Modal
        open={correctionOpen}
        title="Corregir saldo de la meta"
        onClose={() => setCorrectionOpen(false)}
      >
        <ManualCorrectionForm
          pending={correct.isPending}
          error={correct.error}
          onSubmit={(body) =>
            correct.mutate({ id: goal.id, body }, { onSuccess: () => setCorrectionOpen(false) })
          }
        />
      </Modal>
      <Modal open={editOpen} title="Editar meta" onClose={() => setEditOpen(false)}>
        <GoalForm goal={goal} onDone={() => setEditOpen(false)} />
      </Modal>
      <ConfirmDialog
        open={deleteOpen}
        title="¿Eliminar meta?"
        description="Se eliminará el objetivo, pero el dinero y los traspasos registrados conservarán su historial."
        itemLabel={goal.name}
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => del.mutate(goal.id, { onSuccess: () => setDeleteOpen(false) })}
      />
    </div>
  );
}

function LocationSelect({
  id,
  label,
  value,
  onChange,
  accounts,
  bucketsOnly = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  accounts: AccountWithBuckets[];
  /** El destino de una meta debe ser una bolsa reservada, no saldo libre. */
  bucketsOnly?: boolean;
}) {
  const eligibleAccounts = bucketsOnly
    ? accounts.filter((account) => account.buckets.length > 0)
    : accounts;
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      <select
        id={id}
        required
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
      >
        <option value="" disabled>
          {bucketsOnly ? 'Elige un apartado' : 'Elige una cuenta o apartado'}
        </option>
        {eligibleAccounts.map((account) => (
          <optgroup key={account.id} label={account.name}>
            {!bucketsOnly && (
              <option value={account.id}>
                {account.name} · libre {formatMoney(availableAt({ value: account.id, account }))}
              </option>
            )}
            {account.buckets.map((bucket) => (
              <option key={bucket.id} value={`${account.id}:${bucket.id}`}>
                {account.name} › {bucket.name} · {formatMoney(bucket.balance)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

function GoalContributionForm({
  goal,
  accounts,
  safeContribution,
  pending,
  error,
  onSubmit,
}: {
  goal: SavingsGoalView;
  accounts: AccountWithBuckets[];
  safeContribution?: number;
  pending: boolean;
  error: unknown;
  onSubmit: (body: {
    fromAccountId: string;
    fromBucketId?: string;
    toAccountId: string;
    toBucketId?: string;
    amount: number;
    date: string;
    note?: string;
  }) => void;
}) {
  const locations = useMemo(() => locationsFrom(accounts), [accounts]);
  const linkedDestination =
    locations.find((location) => location.bucket?.goalId === goal.id)?.value ?? '';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(linkedDestination);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => toDateInputValue(new Date()));
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!to && linkedDestination) setTo(linkedDestination);
  }, [linkedDestination, to]);
  const selectedFrom = locations.find((location) => location.value === from);
  const hasDestinationBucket = locations.some((location) => Boolean(location.bucket));
  const numericAmount = Number(amount);
  const invalidAmount = !Number.isFinite(numericAmount) || numericAmount <= 0;
  const sameLocation = Boolean(from && to && from === to);
  const exceedsSource = Boolean(selectedFrom && numericAmount > availableAt(selectedFrom) + 0.005);
  const exceedsPrudent = safeContribution !== undefined && numericAmount > safeContribution + 0.005;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!from || !to || sameLocation || invalidAmount || exceedsSource) return;
        const origin = parseLocation(from);
        const destination = parseLocation(to);
        onSubmit({
          fromAccountId: origin.accountId,
          fromBucketId: origin.bucketId,
          toAccountId: destination.accountId,
          toBucketId: destination.bucketId,
          amount: numericAmount,
          date: new Date(`${date}T12:00:00`).toISOString(),
          note: note.trim() || undefined,
        });
      }}
    >
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-3 text-sm leading-5 text-slate-700">
        Esto mueve dinero de verdad entre tus cuentas o apartados y actualiza “{goal.name}”. No
        cuenta como ingreso ni como gasto.
      </div>
      <LocationSelect
        id="goal-from"
        label="De dónde sale"
        accounts={accounts}
        value={from}
        onChange={setFrom}
      />
      <LocationSelect
        id="goal-to"
        label="Apartado donde quedará guardado"
        accounts={accounts}
        value={to}
        onChange={setTo}
        bucketsOnly
      />
      <p className="-mt-2 text-xs leading-4 text-slate-500">
        El destino debe ser un apartado: así este dinero queda reservado para esta meta y no se
        puede contar otra vez como disponible.
      </p>
      {!hasDestinationBucket && (
        <HelperText tone="warning">
          <span className="block">
            Primero crea un apartado para guardar y respaldar esta meta.
          </span>
          <Link
            className="tap-target mt-1 inline-flex items-center font-semibold underline"
            to="/cuentas"
          >
            Abrir Cuentas
          </Link>
        </HelperText>
      )}
      <Field
        id="goal-contribution-amount"
        label="Monto a aportar"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        required
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        helperText={
          selectedFrom
            ? `Disponible desde ese origen: ${formatMoney(availableAt(selectedFrom))}.`
            : 'Elige primero el origen para ver cuánto está disponible.'
        }
      />
      {sameLocation && (
        <p role="alert" className="text-sm text-expense">
          El origen y el destino no pueden ser el mismo.
        </p>
      )}
      {numericAmount < 0 && (
        <p role="alert" className="text-sm text-expense">
          El monto debe ser mayor a cero.
        </p>
      )}
      {exceedsSource && (
        <p role="alert" className="text-sm text-expense">
          El monto supera lo disponible en el origen elegido.
        </p>
      )}
      {exceedsPrudent && (
        <HelperText tone="warning">
          Supera el aporte prudente calculado para este mes. Continúa sólo si este dinero ya está
          reservado y no lo necesitarás para gasto o tarjeta.
        </HelperText>
      )}
      <DatePicker
        id="goal-contribution-date"
        label="Fecha del aporte"
        required
        value={date}
        onChange={setDate}
      />
      <Field
        id="goal-contribution-note"
        label="Nota (opcional)"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Ej. ahorro quincenal"
      />
      {Boolean(error) && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(error)}
        </p>
      )}
      <div className="modal-action-bar">
        <Button
          type="submit"
          disabled={
            pending ||
            !from ||
            !to ||
            !hasDestinationBucket ||
            invalidAmount ||
            sameLocation ||
            exceedsSource
          }
          className="w-full"
        >
          {pending ? 'Registrando…' : 'Mover dinero y aportar'}
        </Button>
      </div>
    </form>
  );
}

function ManualCorrectionForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: unknown;
  onSubmit: (body: { amount: number; reason?: string }) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        const numericAmount = Number(amount);
        if (!numericAmount) return;
        onSubmit({ amount: numericAmount, reason: reason.trim() || undefined });
      }}
    >
      <HelperText tone="warning">
        Esta acción no mueve dinero entre cuentas. Úsala sólo para corregir un saldo inicial o un
        dato que ya verificaste en tu banco.
      </HelperText>
      <Field
        id="goal-correction-amount"
        label="Ajuste"
        type="number"
        inputMode="decimal"
        step="0.01"
        required
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        placeholder="Ej. -500 o 500"
        helperText="Un número positivo suma avance; uno negativo lo reduce."
      />
      <Field
        id="goal-correction-reason"
        label="Motivo (opcional)"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Ej. saldo inicial conciliado"
      />
      {Boolean(error) && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(error)}
        </p>
      )}
      <div className="modal-action-bar">
        <Button type="submit" variant="secondary" disabled={pending} className="w-full">
          {pending ? 'Corrigiendo…' : 'Guardar corrección'}
        </Button>
      </div>
    </form>
  );
}

function GoalForm({ onDone, goal }: { onDone: () => void; goal?: SavingsGoalView }) {
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const editing = Boolean(goal);
  const [name, setName] = useState(goal?.name ?? '');
  const [targetAmount, setTargetAmount] = useState(goal ? String(goal.targetAmount) : '');
  const [saved, setSaved] = useState(goal ? String(goal.saved) : '0');
  const [targetDate, setTargetDate] = useState(() => {
    if (goal) return toDateInputValue(goal.targetDate);
    const d = new Date();
    d.setFullYear(d.getFullYear() + 2);
    return toDateInputValue(d);
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const base = {
      name,
      targetAmount: Number(targetAmount) || 0,
      targetDate,
    };
    if (goal) update.mutate({ id: goal.id, body: base }, { onSuccess: onDone });
    else
      create.mutate(
        { ...base, currency: currentCurrency(), saved: Number(saved) || 0 },
        { onSuccess: onDone },
      );
  };

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Field
        id="g-name"
        label="¿Para qué quieres ahorrar?"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Field
        id="g-target"
        label="¿Cuánto quieres juntar?"
        type="number"
        inputMode="decimal"
        step="0.01"
        required
        value={targetAmount}
        onChange={(e) => setTargetAmount(e.target.value)}
        helperText="Define el total que quieres reunir, sin restar lo que ya hayas ahorrado."
      />
      {!editing && (
        <Field
          id="g-saved"
          label="¿Cuánto ya tenías ahorrado?"
          type="number"
          inputMode="decimal"
          step="0.01"
          value={saved}
          onChange={(e) => setSaved(e.target.value)}
          helperText="Es sólo tu punto de partida. No sacará ni moverá dinero de ninguna cuenta."
        />
      )}
      {editing && (
        <HelperText tone="info">
          Para corregir el ahorro acumulado usa “Corregir saldo”; así queda claro que no hubo un
          movimiento de dinero.
        </HelperText>
      )}
      <DatePicker
        id="g-date"
        label="¿Para cuándo te gustaría lograrla?"
        required
        value={targetDate}
        onChange={setTargetDate}
        helperText="La aplicación divide lo pendiente entre los meses que faltan para estimar tu aportación."
      />
      <HelperText tone="info">
        Al guardar, se recalcula la aportación mensual sugerida con el monto y la fecha nuevos.
      </HelperText>
      {error && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={pending}
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {pending ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear meta'}
        </button>
      </div>
    </form>
  );
}
